export interface Article {
  id: string;
  title: string;
  slug: string;
  categorySlug: string;
  summary: string;
  content: ArticleContent[];
  coverImage: string;
  author: string;
  publishDate: string;
  readTime: number;
  tags: string[];
  featured: boolean;
}

export interface ArticleContent {
  type: "heading" | "paragraph" | "list" | "image" | "tip" | "warning";
  text?: string;
  items?: string[];
  level?: number;
  imageUrl?: string;
  imageAlt?: string;
}

export const articles: Article[] = [
  {
    id: "art-1",
    title: "FIFA官网购票完整流程：从注册到支付一步到位",
    slug: "fifa-official-ticketing-guide",
    categorySlug: "fifa-official",
    summary: "详解国际足联官网世界杯门票的完整购买流程，包括账号注册、实名认证、购票阶段选择与支付方式，助你轻松完成官方购票。",
    content: [
      { type: "heading", text: "第一步：注册FIFA账号", level: 2 },
      { type: "paragraph", text: "访问FIFA官网（FIFA.com），点击右上角的\"Sign Up\"按钮。你需要提供有效的电子邮箱地址，并设置一个强密码。注册完成后，系统会发送验证邮件到你的邮箱，请务必在24小时内完成验证。" },
      { type: "tip", text: "建议使用Gmail或Outlook等国际邮箱，国内邮箱可能存在收不到验证邮件的情况。" },
      { type: "heading", text: "第二步：完善个人信息", level: 2 },
      { type: "paragraph", text: "登录后进入个人中心，填写真实的姓名、出生日期、国籍和护照信息。注意：购票时填写的个人信息必须与护照完全一致，否则无法入场。每位用户最多可以为包括自己在内的4人申请门票。" },
      { type: "heading", text: "第三步：了解购票阶段", level: 2 },
      { type: "paragraph", text: "FIFA官方售票通常分为以下几个阶段：" },
      { type: "list", items: ["第一阶段：随机抽签销售期（Random Selection Draw）- 提交购票申请，抽签决定是否中签", "第二阶段：先到先得销售期（First-Come, First-Served）- 剩余门票按提交时间顺序分配", "第三阶段：官方转售平台（Official Resale Platform）- 官方渠道的二手票交易"] },
      { type: "heading", text: "第四步：选择比赛与座位", level: 2 },
      { type: "paragraph", text: "在购票页面中，你可以按日期、城市、球队筛选比赛。每场比赛提供多个票价等级：第一类（Cat 1）为最佳观赛位置，第二类（Cat 2）为中等位置，第三类（Cat 3）为性价比之选。此外，还有专门的无障碍座位区域。" },
      { type: "warning", text: "注意：Cat 1和Cat 2的门票竞争最为激烈，建议在申请时选择多个场次和类别，提高中签概率。" },
      { type: "heading", text: "第五步：完成支付", level: 2 },
      { type: "paragraph", text: "中签后，FIFA会通过邮件通知你。你需要在规定时间内（通常为7天）完成支付。支持的支付方式包括Visa、Mastercard信用卡以及部分地区的本地支付方式。支付成功后，你将收到电子门票，在比赛前通过FIFA官方App即可使用。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=FIFA%20World%20Cup%20official%20website%20interface%20on%20a%20modern%20laptop%20screen%2C%20soccer%20stadium%20background%20in%20warm%20golden%20light%2C%20clean%20minimalist%20desk%20setup%2C%20professional%20environment%2C%20soft%20natural%20lighting%2C%20high%20detail%2C%20editorial%20style%2C%20warm%20neutral%20tones&width=1600&height=900&seq=art-1-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-07-15",
    readTime: 8,
    tags: ["FIFA官网", "购票流程", "官方渠道", "注册指南"],
    featured: true
  },
  {
    id: "art-2",
    title: "2026世界杯官方授权代理商名单及对比分析",
    slug: "authorized-agents-comparison",
    categorySlug: "authorized-agents",
    summary: "汇总2026年世界杯官方授权票务代理商，从价格、服务、可靠性等维度进行深度对比，帮你选择最合适的购票渠道。",
    content: [
      { type: "heading", text: "什么是官方授权代理商？", level: 2 },
      { type: "paragraph", text: "国际足联在每届世界杯都会指定一批官方授权票务代理商，这些代理商经过FIFA严格审核，能够合法销售世界杯门票及相关旅行套餐。通过授权代理商购票，可以获得本地语言支持和更便捷的服务。" },
      { type: "heading", text: "2026年世界杯主要授权代理商", level: 2 },
      { type: "paragraph", text: "根据FIFA官方公布的信息，以下为2026年世界杯的主要授权票务代理商：" },
      { type: "list", items: ["MATCH Hospitality - 全球最大世界杯官方款待服务提供商，提供高端观赛体验", "Category A授权代理商 - 覆盖亚洲市场的官方票务合作伙伴", "Category B授权代理商 - 欧洲区域授权票务服务商", "Category C授权代理商 - 美洲本地授权代理商"] },
      { type: "tip", text: "建议通过FIFA官网的\"Authorized Resellers\"页面验证代理商资质，确保选择的代理商在官方授权名单中。" },
      { type: "heading", text: "代理商对比维度", level: 2 },
      { type: "paragraph", text: "在选择代理商时，我们建议从以下几个维度进行综合评估：票价水平、服务费比例、语言支持、退款政策、客服响应速度以及历史用户评价。部分代理商提供中文客服和人民币支付，对中国球迷更为友好。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=professional%20business%20meeting%20with%20World%20Cup%20posters%20and%20tickets%20on%20a%20modern%20conference%20table%2C%20warm%20office%20lighting%2C%20clean%20corporate%20environment%2C%20soccer%20memorabilia%20in%20background%2C%20golden%20hour%20sunlight%20streaming%20through%20windows%2C%20editorial%20photography&width=1600&height=900&seq=art-2-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-07-20",
    readTime: 10,
    tags: ["授权代理商", "票务对比", "购票渠道", "代理分析"],
    featured: true
  },
  {
    id: "art-3",
    title: "世界杯球迷套餐怎么选？全套观赛体验指南",
    slug: "fan-packages-guide",
    categorySlug: "fan-packages",
    summary: "从经济型到豪华款待，全面解析各类世界杯球迷套餐的配置、价格与适用场景，帮你找到最合适的观赛方案。",
    content: [
      { type: "heading", text: "球迷套餐的优势", level: 2 },
      { type: "paragraph", text: "球迷套餐将门票、住宿、交通、餐饮等打包在一起，省去了自行预订的繁琐流程。对于首次出国观赛的球迷来说，这是一个省心省力的选择。套餐通常提供从经济型到豪华型多个档次。" },
      { type: "heading", text: "套餐类型详解", level: 2 },
      { type: "list", items: ["经济套餐：基础门票+青年旅舍住宿+公共交通通票，适合预算有限的年轻球迷", "标准套餐：中等座位门票+三星级酒店+接送机+早餐，适合大多数球迷", "高级套餐：优质座位门票+四星级酒店+专车服务+当地导游，适合追求舒适体验的球迷", "豪华款待套餐：VIP座位+五星级酒店+私人管家+专属观赛派对，提供顶级观赛体验"] },
      { type: "heading", text: "选择建议", level: 2 },
      { type: "paragraph", text: "选择套餐时，建议优先确认套餐提供商的官方授权资质。其次，仔细阅读套餐包含的各项服务细节，特别是退改政策。对于远赴海外观赛的球迷，建议选择包含旅行保险的套餐。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=luxury%20soccer%20stadium%20hospitality%20suite%20with%20premium%20seating%20view%20of%20the%20pitch%2C%20champagne%20and%20gourmet%20food%20on%20table%2C%20excited%20fans%20in%20smart%20casual%20attire%2C%20warm%20ambient%20lighting%2C%20professional%20sports%20photography%20style%2C%20clean%20elegant%20composition&width=1600&height=900&seq=art-3-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-07-25",
    readTime: 7,
    tags: ["球迷套餐", "观赛体验", "旅行套餐", "住宿门票"],
    featured: true
  },
  {
    id: "art-4",
    title: "世界杯抢票终极攻略：提高中签率的10个技巧",
    slug: "ticket-sniping-strategies",
    categorySlug: "ticket-tips",
    summary: "整理多年世界杯抢票经验，分享提高官方抽签中签率和先到先得阶段成功率的实用技巧。",
    content: [
      { type: "heading", text: "了解购票机制", level: 2 },
      { type: "paragraph", text: "世界杯官方售票采用随机抽签与先到先得相结合的机制。了解这两个阶段的规则差异，是制定抢票策略的第一步。随机抽签阶段不看提交速度，先到先得阶段则拼的是手速和网速。" },
      { type: "heading", text: "十大抢票技巧", level: 2 },
      { type: "list", items: [
        "技巧一：提前注册账号并完成实名认证，避免临时抱佛脚",
        "技巧二：使用多设备、多浏览器同时排队，提高进入购票系统的概率",
        "技巧三：优先选择冷门场次和低需求比赛，中签率更高",
        "技巧四：关注FIFA官方社交媒体，获取最新售票时间公告",
        "技巧五：加入球迷社群，实时交流抢票信息和经验",
        "技巧六：确保支付方式可用，提前绑定信用卡并确认额度充足",
        "技巧七：选择Cat 3和Cat 4票价等级，竞争相对较小",
        "技巧八：分散风险，同时向多个场次提交申请",
        "技巧九：关注官方转售平台，中签后放弃的门票会回流",
        "技巧十：考虑团队观赛门票（Team Specific Tickets），跟随支持的球队"
      ]},
      { type: "warning", text: "注意：不要使用自动抢票脚本或机器人，FIFA有严格的反作弊机制，违规账号将被永久封禁。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=modern%20workspace%20with%20multiple%20screens%20displaying%20World%20Cup%20schedules%20and%20countdown%20timer%2C%20coffee%20cup%20on%20desk%2C%20warm%20ambient%20lighting%2C%20focused%20atmosphere%2C%20clean%20minimalist%20setup%2C%20golden%20hour%20light%20through%20windows%2C%20professional%20environment&width=1600&height=900&seq=art-4-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-07-28",
    readTime: 12,
    tags: ["抢票技巧", "中签率", "购票攻略", "官方售票"],
    featured: true
  },
  {
    id: "art-5",
    title: "警惕！世界杯假票常见骗局与识别方法",
    slug: "fake-ticket-scams",
    categorySlug: "scam-avoidance",
    summary: "揭露世界杯门票市场中常见的诈骗手法，教你如何识别假票、验证票务真伪，保护自身权益。",
    content: [
      { type: "heading", text: "常见假票骗局", level: 2 },
      { type: "paragraph", text: "每逢世界杯，假票和票务诈骗案件层出不穷。骗子利用球迷迫切购票的心理，设置各种陷阱。了解这些常见骗局，是保护自己的第一步。" },
      { type: "list", items: [
        "假冒官方网站：仿冒FIFA或授权代理商的钓鱼网站，诱导用户输入个人信息和支付信息",
        "社交媒体黄牛：在微信群、微博等平台高价兜售所谓的\"内部票\"或\"员工票\"",
        "重复售票：同一张电子门票被多次出售给不同买家",
        "虚假套餐：以远低于市场价的价格兜售\"球迷套餐\"，实为骗局",
        "PS伪造门票：利用Photoshop等技术伪造电子门票截图"
      ]},
      { type: "heading", text: "如何识别假票", level: 2 },
      { type: "paragraph", text: "世界杯门票采用多重防伪技术，包括动态二维码、全息防伪标签和专属序列号。最可靠的方式是通过FIFA官方App扫描门票二维码进行验证。在非官方渠道购票时，务必要求对方提供完整的购票凭证和身份信息。" },
      { type: "tip", text: "记住一个基本原则：如果价格看起来好得不像是真的，那它很可能就是假的。世界杯热门场次的门票很少会有大幅折扣。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=closeup%20of%20a%20hand%20holding%20a%20magnifying%20glass%20over%20World%20Cup%20tickets%20with%20security%20holograms%2C%20dark%20moody%20background%20with%20warm%20accent%20light%2C%20dramatic%20lighting%20highlighting%20ticket%20details%2C%20investigative%20journalism%20style%2C%20high%20contrast%2C%20professional%20photography&width=1600&height=900&seq=art-5-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-08-01",
    readTime: 9,
    tags: ["假票识别", "防骗指南", "安全购票", "黄牛陷阱"],
    featured: false
  },
  {
    id: "art-6",
    title: "FIFA官方转售平台使用指南：安全购买二手票",
    slug: "fifa-resale-platform",
    categorySlug: "fifa-official",
    summary: "详细介绍FIFA官方转售平台的操作方法，让你安全、合法地购买或出售世界杯二手门票。",
    content: [
      { type: "heading", text: "什么是官方转售平台？", level: 2 },
      { type: "paragraph", text: "FIFA官方转售平台是国际足联提供的唯一合法二手门票交易渠道。中签后无法前往观赛的球迷可以通过该平台以原价（含手续费）转售门票，而未能中签的球迷则可以在此购买回流门票。" },
      { type: "heading", text: "使用注意事项", level: 2 },
      { type: "list", items: ["门票价格严格按原价出售，禁止加价", "平台收取少量服务手续费", "购买后门票自动转移至买家账户，杜绝一票多卖", "交易全程由FIFA担保，资金安全有保障"] },
      { type: "heading", text: "购买时机建议", level: 2 },
      { type: "paragraph", text: "转售平台上的门票并非随时都有，建议在以下时间节点重点关注：抽签结果公布后的一周内、比赛前一个月、以及比赛前72小时。这些时段是门票回流的高峰期。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=modern%20digital%20tablet%20displaying%20ticket%20exchange%20platform%20interface%20with%20secure%20transaction%20icons%2C%20warm%20coffee%20shop%20background%2C%20soft%20natural%20light%20through%20windows%2C%20clean%20minimalist%20composition%2C%20technology%20and%20sports%20theme%2C%20professional%20product%20photography&width=1600&height=900&seq=art-6-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-08-03",
    readTime: 6,
    tags: ["FIFA转售", "二手门票", "安全交易", "官方平台"],
    featured: false
  },
  {
    id: "art-7",
    title: "世界杯门票价格全解析：不同场次票价差异有多大？",
    slug: "ticket-price-analysis",
    categorySlug: "fifa-official",
    summary: "全面分析世界杯小组赛、淘汰赛到决赛各阶段的门票价格区间，帮你合理规划观赛预算。",
    content: [
      { type: "heading", text: "票价影响因素", level: 2 },
      { type: "paragraph", text: "世界杯门票价格受多个因素影响，包括比赛阶段、对阵双方、座位位置和主办城市。小组赛门票价格最低，决赛门票价格最高，两者之间的差距可能达到数十倍。" },
      { type: "heading", text: "各阶段票价参考", level: 2 },
      { type: "list", items: ["小组赛：Cat 3约XXX美元起，Cat 1约XXX美元", "1/8决赛：Cat 3约XXX美元起，Cat 1约XXX美元", "1/4决赛：Cat 3约XXX美元起，Cat 1约XXX美元", "半决赛：Cat 3约XXX美元起，Cat 1约XXX美元", "决赛：Cat 3约XXX美元起，Cat 1约XXX美元以上"] }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=colorful%20pricing%20chart%20and%20World%20Cup%20tickets%20arranged%20artistically%20on%20a%20warm%20wooden%20surface%2C%20calculator%20and%20papers%20nearby%2C%20soft%20golden%20lighting%2C%20financial%20planning%20theme%2C%20clean%20organized%20composition%2C%20editorial%20style&width=1600&height=900&seq=art-7-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-07-18",
    readTime: 7,
    tags: ["门票价格", "票价分析", "预算规划", "比赛阶段"],
    featured: false
  },
  {
    id: "art-8",
    title: "中国球迷购买世界杯门票的专属渠道与注意事项",
    slug: "chinese-fans-channels",
    categorySlug: "authorized-agents",
    summary: "针对中国球迷的专属购票指南，涵盖中国区授权代理商、支付方式、签证准备等全方位信息。",
    content: [
      { type: "heading", text: "中国区购票渠道", level: 2 },
      { type: "paragraph", text: "中国球迷除了可以通过FIFA官网购票外，还有专门针对中国市场的授权票务代理渠道。这些渠道通常提供中文服务、人民币支付以及更便捷的沟通方式。" },
      { type: "heading", text: "关键注意事项", level: 2 },
      { type: "list", items: ["提前办理护照和目的地签证，确保护照有效期超过6个月", "关注外汇额度，大额购票可能需要使用多张信用卡或提前换汇", "保留所有购票凭证和交易记录，以备不时之需", "购买旅行保险，覆盖行程取消和医疗费用", "提前了解目的地国家的入境政策和防疫要求"] }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=Chinese%20soccer%20fans%20cheering%20with%20national%20flags%20in%20a%20modern%20stadium%2C%20warm%20golden%20sunset%20light%20illuminating%20the%20stands%2C%20passionate%20atmosphere%2C%20sports%20photography%20style%2C%20vibrant%20colors%2C%20joyful%20expressions&width=1600&height=900&seq=art-8-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-08-02",
    readTime: 8,
    tags: ["中国球迷", "购票渠道", "签证准备", "出行指南"],
    featured: false
  },
  {
    id: "art-9",
    title: "世界杯购票时间线：从售票公告到比赛日的完整规划",
    slug: "ticket-timeline-planning",
    categorySlug: "ticket-tips",
    summary: "按照时间顺序梳理世界杯售票全流程，帮你不错过任何关键时间节点，有序完成购票和出行准备。",
    content: [
      { type: "heading", text: "购票时间线概览", level: 2 },
      { type: "paragraph", text: "世界杯售票是一个长达数月的过程。从FIFA公布售票日期到最终拿到门票，你需要经历多个关键阶段。提前了解并规划这些时间节点，是成功购票的关键。" },
      { type: "list", items: ["赛前18个月：FIFA公布主办城市和比赛日程", "赛前12个月：FIFA发布官方售票公告，公布各阶段时间表", "赛前10个月：随机抽签阶段开始，开放购票申请", "赛前8个月：抽签结果公布，中签者完成支付", "赛前6个月：先到先得阶段开始", "赛前4个月：官方转售平台开放", "赛前2个月：电子门票发放", "赛前1周：门票激活，可通过官方App使用"] }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=elegant%20wall%20calendar%20with%20World%20Cup%20dates%20marked%20in%20gold%20circles%2C%20warm%20desk%20setup%20with%20coffee%20and%20tickets%2C%20soft%20natural%20light%2C%20organized%20planning%20theme%2C%20clean%20minimalist%20aesthetic%2C%20productivity%20vibe&width=1600&height=900&seq=art-9-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-07-30",
    readTime: 6,
    tags: ["时间规划", "购票日历", "关键节点", "日程安排"],
    featured: false
  },
  {
    id: "art-10",
    title: "黄牛党的套路有多深？深度揭秘门票灰色产业链",
    slug: "scalper-industry-exposed",
    categorySlug: "scam-avoidance",
    summary: "深入调查世界杯门票灰色市场，揭示黄牛党的运作模式、定价策略和常见套路，助你远离票务陷阱。",
    content: [
      { type: "heading", text: "黄牛的主要运作模式", level: 2 },
      { type: "paragraph", text: "世界杯黄牛已经形成了一条完整的灰色产业链。他们通过各种手段囤积热门场次门票，然后在二级市场高价出售。了解他们的运作方式，有助于你识别和避开这些陷阱。" },
      { type: "list", items: ["批量注册虚假账号参与抽签，提高中签概率", "利用自动化脚本在先到先得阶段快速抢票", "通过多个代理商渠道同时下单，锁定更多门票", "建立看似正规的票务网站，伪装成授权代理商"] },
      { type: "heading", text: "为什么不该从黄牛处购票？", level: 2 },
      { type: "paragraph", text: "从非官方渠道购票存在多重风险：门票真伪无法确认、价格远超票面价值、售后维权困难、个人信息可能被滥用。一旦在入场时被发现使用假票或他人名义的门票，你将面临被拒绝入场甚至法律追究的风险。" },
      { type: "warning", text: "特别提醒：FIFA严禁门票转售牟利行为。从黄牛处购买的高价票可能在入场时被查出异常，导致无法进入球场。切勿因一时心急而选择非法渠道。" }
    ],
    coverImage: "https://readdy.ai/api/search-image?query=dark%20alley%20with%20shadowy%20figure%20holding%20tickets%2C%20dramatic%20noir%20lighting%20with%20warm%20streetlamp%20glow%2C%20mysterious%20atmosphere%2C%20cinematic%20composition%2C%20urban%20night%20scene%2C%20high%20contrast%20photography%2C%20film%20noir%20style&width=1600&height=900&seq=art-10-cover&orientation=landscape",
    author: "世界杯票务指南编辑组",
    publishDate: "2026-08-05",
    readTime: 10,
    tags: ["黄牛揭秘", "灰色市场", "票务安全", "防骗指南"],
    featured: false
  }
];

export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find(a => a.slug === slug);
};

export const getArticlesByCategory = (categorySlug: string): Article[] => {
  return articles.filter(a => a.categorySlug === categorySlug);
};

export const getFeaturedArticles = (): Article[] => {
  return articles.filter(a => a.featured);
};

export const getRelatedArticles = (currentSlug: string, categorySlug: string, limit: number = 3): Article[] => {
  return articles
    .filter(a => a.slug !== currentSlug && a.categorySlug === categorySlug)
    .slice(0, limit);
};